/* eslint no-console: 0 */
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useGetDevice } from '../../../hooks/useGetDevice.ts';
import { useUpdateDeviceProfileMutation } from '../../../hooks/useUpdateDeviceProfileMutation.ts';
import screenNavigator from '../../../services/ScreenNavigator';
import Button from '../../bits/Button.jsx';
import TextField from '../../bits/TextField.jsx';
import Loading from '../Loading.jsx';
import MenuHeaderBar from '../MenuHeaderBar.jsx';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle.ts';

interface EditDeviceNameProps { }

/**
 * Component that allows editing device name.
 */
export const EditDeviceName: React.FC<EditDeviceNameProps> = () => {
  const { id: deviceId } = useParams();
  const { data: device, isLoading } = useGetDevice({ deviceId });
  const [deviceName, setDeviceName] = useState<string>('');
  const history = useHistory();
  const queryClient = useQueryClient();
  const mutation = useUpdateDeviceProfileMutation();

  useSetModalTitle('Edit Device');

  const onSave = useCallback(() => {
    mutation.mutate({ id: device.activeProfile.id, name: deviceName }, {
      onSuccess: () => {
        // Invalidate queries and destroy data so that we won't show stale data
        queryClient.invalidateQueries({ queryKey: ['useGetDevice', deviceId] });
        queryClient.setQueryData(['useGetDevice', deviceId], null);

        queryClient.invalidateQueries({ queryKey: ['useGetUserDevices'] });
        queryClient.setQueriesData(['useGetUserDevices'], null);

        screenNavigator.exitCurrentScreen(history);
      },
    });
  }, [history, queryClient, deviceName]);

  useEffect(() => {
    // Make sure not to set deviceName to undefined since that causes some MaterialUI errors
    setDeviceName(device?.name || '');
  }, [device?.name]);


  return (
    <>
      {isLoading && <Loading />}

      {!isLoading && device && <>
        <TextField
          autoFocus
          id="deviceName"
          type="text"
          label="Name"
          value={deviceName}
          fullWidth
          margin="normal"
          disabled={mutation.isLoading}
          onChange={e => setDeviceName(e.target.value)}
        />

        <div style={{ marginTop: '30px' }}>
          <Button
            onClick={onSave}
            color="primary"
            disabled={device.name === deviceName || mutation.isLoading}
            loading={mutation.isLoading}>Update</Button>
        </div>
      </>}
    </>
  );
};
